import { IImageData } from "common/records/Image";
import { Record, List } from "immutable";
import { round } from "lodash";
import { ISkuData } from "common/records/Sku";
import { checkDate } from "common/helpers/Date";
import { DateStringNumber, Nullable } from "common/records";
import { EstimateRecord, markupMultiplier } from "common/records/Estimate";
import { EstimateLineItemRecord, isMarkupable } from "common/records/EstimateLineItem";

export const productOptionFromJSON = (json: Partial<IProductOptionData>): ProductOptionRecord => {
  const recordData: IProductOptionRecord = { ...(json as any) };

  if (!json.match_id && json.uuid !== undefined) {
    recordData.match_id = json.uuid;
  }

  if (json.created_at) {
    recordData.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    recordData.updated_at = checkDate(json.updated_at);
  }

  let record = new ProductOptionRecord(recordData);

  if (json.images) {
    record = record.set("image_ids", List(json.images.map((i) => i.id)));
  }

  if (json.sku) {
    record = record.set("sku_id", json.sku.id);
  }

  return record;
};

export interface IProductOptionData {
  id: number;
  matched: IProductOptionData[];
  uuid: string;
  match_id: string;
  option_group_id: number;
  name: string;
  uom: string;
  description: string;
  cost: number;
  msrp: number;
  labor_price: number;
  product_price: number;
  price: number;
  sort_order: Nullable<number>;
  images: IImageData[];
  charge_type: string;
  pog_type: string;
  show: boolean;
  sku: ISkuData;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
}

export interface IProductOptionRecord {
  id: number;
  matched: List<ProductOptionRecord>;
  uuid: string;
  match_id: string;
  option_group_id: number;
  name: string;
  uom: string;
  description: string;
  cost: number;
  msrp: number;
  labor_price: number;
  product_price: number;
  price: number;
  sort_order: Nullable<number>;
  image_ids: List<number>;
  charge_type: string;
  pog_type: string;
  show: boolean;
  sku_id: number;
  created_at: Date;
  updated_at: Date;
}

const defaultProductOptionProps: IProductOptionRecord = {
  id: 0,
  matched: List<ProductOptionRecord>(),
  uuid: "",
  match_id: "",
  option_group_id: 0,
  name: "",
  uom: "",
  description: "",
  cost: 0,
  msrp: 0,
  labor_price: 0,
  product_price: 0,
  price: 0,
  sort_order: null,
  image_ids: List(),
  charge_type: "",
  pog_type: "",
  show: true,
  sku_id: 0,
  created_at: new Date(),
  updated_at: new Date(),
};

export class ProductOptionRecord extends Record(defaultProductOptionProps) implements IProductOptionRecord {}

/**
 * Computes the displayed unit price of a product option for the line item editor's option table.
 * When the line item is markupable, product and labor prices are each multiplied by the estimate's
 * respective markup multiplier; otherwise the raw prices are used. The two are summed.
 * (Direct port of rsf-app `records/ProductOption.calculatePrice`.)
 */
export const calculatePrice = (
  productOption: ProductOptionRecord,
  estimate: EstimateRecord,
  lineItem: EstimateLineItemRecord,
): number => {
  let productPrice = productOption.product_price || 0;
  let laborPrice = productOption.labor_price || 0;

  if (isMarkupable(lineItem)) {
    productPrice = round((productOption.product_price || 0) * markupMultiplier(estimate.product_markup), 2);
    laborPrice = round((productOption.labor_price || 0) * markupMultiplier(estimate.labor_markup), 2);
  }

  return productPrice + laborPrice;
};
